import * as Sentry from '@sentry/react';
import type { JsonObject } from './types/json';

const environment = import.meta.env.MODE || 'development';
const isDevelopment = environment === 'development';
const defaultTraceSampleRate = environment === 'production' ? 0.1 : 1.0;

function isMetricMirrorSamplingContext(context: {
  attributes?: Record<string, unknown>;
}): boolean {
  return (
    typeof context.attributes?.metric_name === 'string' &&
    typeof context.attributes?.metric_type === 'string'
  );
}

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) {
    console.log('[Sentry] No DSN provided, skipping initialization');
    return;
  }

  Sentry.init({
    dsn,
    environment,
    enabled: !isDevelopment || import.meta.env.VITE_SENTRY_ENABLED === 'true',

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] }),
      ...(environment === 'production'
        ? [
            Sentry.replayIntegration({
              maskAllText: true,
              blockAllMedia: true,
            }),
          ]
        : []),
    ],
    enableLogs: true,
    enableMetrics: true,
    tracesSampler(samplingContext) {
      if (isMetricMirrorSamplingContext(samplingContext)) {
        return 1.0;
      }

      return samplingContext.inheritOrSampleWith(defaultTraceSampleRate);
    },
    release:
      import.meta.env.VITE_GIT_SHA ||
      import.meta.env.VITE_APP_VERSION ||
      'unknown',
    dist: environment,
    sendDefaultPii: false,
    beforeSend(event, hint) {
      if (isDevelopment && import.meta.env.VITE_SENTRY_ENABLED !== 'true') {
        return null;
      }

      const error = hint.originalException;
      if (error && typeof error === 'object' && 'message' in error) {
        const message = error.message as string;

        const ignoredMessages = [
          'ResizeObserver loop limit exceeded',
          'ResizeObserver loop completed with undelivered notifications',
          'Non-Error promise rejection captured',
        ];

        if (ignoredMessages.some((ignored) => message.includes(ignored))) {
          return null;
        }
      }

      if (event.request?.cookies) {
        delete event.request.cookies;
      }

      if (event.extra) {
        const extra = event.extra;
        Object.keys(extra).forEach((key) => {
          if (
            key.toLowerCase().includes('token') ||
            key.toLowerCase().includes('key') ||
            key.toLowerCase().includes('secret')
          ) {
            delete extra[key];
          }
        });
      }

      return event;
    },

    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === 'console' && breadcrumb.level === 'debug') {
        return null;
      }

      if (
        breadcrumb.category === 'fetch' &&
        breadcrumb.data?.url?.includes('sentry.io')
      ) {
        return null;
      }

      return breadcrumb;
    },

    ignoreErrors: [
      'top.GLOBALS',
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
      'Network request failed',
      'NetworkError',
      'Failed to fetch',
      'User rejected the request',
      'User cancelled',
    ],

    allowUrls:
      environment === 'production'
        ? [/https:\/\/.*\.solanamobile\.com/, /https:\/\/.*\.amazonaws\.com/]
        : [],
  });
}

export function setSentryUser(user: { id: string; email?: string } | null) {
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
    });
  } else {
    Sentry.setUser(null);
  }
}

export function setSentryContext(key: string, context: JsonObject) {
  Sentry.setContext(key, context);
}

export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = 'info',
) {
  Sentry.captureMessage(message, level);
}

export function captureException(error: Error, context?: JsonObject) {
  if (context) {
    Sentry.withScope((scope) => {
      scope.setContext('additional', context);
      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }
}
